import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Globe, Monitor, CheckCircle, Smartphone, MessageCircle,
  BarChart3, Zap, Shield, Clock, ChevronRight, Code, Cpu,
  HardDrive, Settings, Mail
} from "lucide-react";
import MainLayout from "../components/MainLayout";
import SEO, { pageSEO } from "../components/SEO";

const WHATSAPP_NUMBER = "447435324947";

const WebDevelopmentPage = () => {
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    {
      id: "websites",
      title: "Professional Websites",
      icon: Globe,
      color: "blue",
      features: [
        "Business presentation websites",
        "Corporate websites",
        "Service pages",
        "Modern, responsive design",
        "SEO optimization included"
      ]
    },
    {
      id: "platforms",
      title: "Custom Platforms",
      icon: Monitor,
      color: "purple",
      features: [
        "Ticketing systems",
        "CRM solutions",
        "Custom dashboards",
        "Internal management platforms",
        "Business process automation"
      ]
    },
    {
      id: "miniapps",
      title: "Browser-Based Mini-Apps",
      icon: Cpu,
      color: "green",
      note: "All mini-apps run directly in the browser. No native Android/iOS apps.",
      features: [
        "Client portals",
        "Smart forms",
        "Booking & scheduling systems",
        "Internal tools and micro-applications"
      ]
    },
    {
      id: "ecommerce",
      title: "Custom E-commerce Solutions",
      icon: HardDrive,
      color: "amber",
      note: "Fully custom builds — no Shopify, no WooCommerce.",
      features: [
        "Custom shopping systems",
        "Secure checkout",
        "Product & order management",
        "Automated workflows",
        "Tailored to your business needs"
      ]
    },
    {
      id: "maintenance",
      title: "Maintenance & Technical Support",
      icon: Settings,
      color: "cyan",
      features: [
        "Regular updates",
        "Backups",
        "Monitoring",
        "Speed optimization"
      ]
    }
  ];

  const whyChooseUs = [
    "Fast delivery",
    "Transparent pricing",
    "Premium design tailored to your brand",
    "Scalable solutions",
    "Real experience in IT, automation, and UX"
  ];

  const workflow = [
    { step: 1, title: "Free consultation" },
    { step: 2, title: "Custom plan & quote" },
    { step: 3, title: "Design and development" },
    { step: 4, title: "Testing and optimization" },
    { step: 5, title: "Launch + ongoing support" }
  ];

  const pricing = [
    { service: "1–3 page website", price: "£150" },
    { service: "Full business website", price: "£300" },
    { service: "Custom e-commerce website", price: "£600" },
    { service: "Browser-based mini-app", price: "£200" },
    { service: "Monthly maintenance", price: "£20" }
  ];

  const getColorStyles = (color) => {
    const colors = {
      blue: { bg: 'rgba(59, 130, 246, 0.1)', text: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' },
      purple: { bg: 'rgba(168, 85, 247, 0.1)', text: '#c084fc', border: 'rgba(168, 85, 247, 0.3)' },
      green: { bg: 'rgba(34, 197, 94, 0.1)', text: '#4ade80', border: 'rgba(34, 197, 94, 0.3)' },
      amber: { bg: 'rgba(245, 158, 11, 0.1)', text: '#fbbf24', border: 'rgba(245, 158, 11, 0.3)' },
      cyan: { bg: 'rgba(6, 182, 212, 0.1)', text: '#22d3ee', border: 'rgba(6, 182, 212, 0.3)' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <MainLayout>
      <SEO {...pageSEO.webDevelopment} />
      
      {/* Hero Section */}
      <section className="pt-8 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 right-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium mb-6">
              <Globe className="w-4 h-4" />
              Digital Solutions
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
              Website, Platform &<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-green-400">Web App Development</span>
            </h1>
            <p className="text-base md:text-lg text-slate-400 max-w-3xl mx-auto mb-8">
              We build modern websites, custom platforms, and browser-based mini-apps designed to automate and simplify your business processes. All applications run directly in the browser — no Android or iOS installation required.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I'm interested in your web development services`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-green-500/20"
                data-testid="webdev-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
              <a 
                href="mailto:support@thesmartit.co.uk?subject=Web Development Inquiry"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-all flex items-center gap-2 border border-blue-500"
                data-testid="webdev-email-btn"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services-section" className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Our Services</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((service, idx) => {
              const colorStyle = getColorStyles(service.color);
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-all"
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: colorStyle.bg }}
                  >
                    <service.icon className="w-6 h-6" style={{ color: colorStyle.text }} />
                  </div>
                  
                  <h3 className="text-lg font-bold text-white mb-3">{service.title}</h3>
                  
                  {service.note && (
                    <p className="text-xs text-slate-500 italic mb-3">{service.note}</p>
                  )}
                  
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colorStyle.text }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 bg-slate-900/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Why Choose Us</h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg"
              >
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-slate-200 text-sm">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Our Workflow</h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {workflow.map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center gap-3 bg-slate-900/60 border border-slate-800 rounded-xl px-5 py-3"
              >
                <div className="w-8 h-8 bg-blue-500/20 border border-blue-500/30 rounded-full flex items-center justify-center">
                  <span className="text-blue-400 font-bold text-sm">{item.step}</span>
                </div>
                <span className="text-white font-medium">{item.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 bg-slate-900/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Starting Prices</h2>
          </div>
          
          <div className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-xl overflow-hidden">
            {pricing.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`flex items-center justify-between px-5 py-4 ${idx !== pricing.length - 1 ? 'border-b border-slate-800' : ''}`}
              >
                <span className="text-slate-300">{item.service}</span>
                <span className="text-xl font-bold text-blue-400">{item.price}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-green-500/10 border border-slate-700 rounded-2xl p-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to Start Your Project?</h2>
            <p className="text-slate-400 mb-6">
              Use our client portal to submit your project details, or get in touch for a free consultation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/dashboard/new-project"
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold px-6 py-3 rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-blue-500/20"
              >
                <Globe className="w-5 h-5" />
                Start Project Online
              </Link>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I'd like to discuss a web development project`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-xl transition-all flex items-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </MainLayout>
  );
};


export default WebDevelopmentPage;
